import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
  differenceInDays,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

const resolveDateFnsLocale = locale => {
  if (!locale) return null;
  const normalized = locale.replace(/_/g, '-');
  if (normalized === 'pt-BR') return ptBR;
  return null;
};

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'h:mm a', locale) => {
  const unixTime = fromUnixTime(time);
  const resolvedLocale = resolveDateFnsLocale(locale);
  if (!resolvedLocale) return format(unixTime, dateFormat);
  return format(unixTime, dateFormat, { locale: resolvedLocale });
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'MMM d, yyyy', locale) => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const resolvedLocale = resolveDateFnsLocale(locale);
  const formatOptions = resolvedLocale ? { locale: resolvedLocale } : undefined;
  const messageDate = format(messageTime, dateFormat, formatOptions);
  if (!isSameYear(messageTime, now)) {
    const differentYearFormat = resolvedLocale
      ? 'dd/MM/yyyy, HH:mm'
      : 'LLL d y, h:mm a';
    return format(messageTime, differentYearFormat, formatOptions);
  }
  return messageDate;
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = (time, locale) => {
  const unixTime = fromUnixTime(time);
  const resolvedLocale = resolveDateFnsLocale(locale);
  const formatOptions = resolvedLocale
    ? { addSuffix: true, locale: resolvedLocale }
    : { addSuffix: true };
  return formatDistanceToNow(unixTime, formatOptions);
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy', locale) => {
  const unixTime = fromUnixTime(time);
  const resolvedLocale = resolveDateFnsLocale(locale);
  if (!resolvedLocale) return format(unixTime, df);
  return format(unixTime, df, { locale: resolvedLocale });
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false, locale) => {
  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const normalizedTime = time.toLowerCase().trim();
  const normalizedLocale = locale?.replace(/_/g, '-');
  const isPortugueseLocale = normalizedLocale === 'pt-BR';
  const isPortugueseText = /h\u00e1\s|minuto|hora|dia|m(?:e|\u00ea)s|ano/.test(
    normalizedTime
  );
  const isPortuguese = isPortugueseLocale || isPortugueseText;
  const suffix = withAgo
    ? isPortuguese
      ? ' atr\u00e1s'
      : ' ago'
    : '';
  const unitMap = isPortugueseLocale
    ? {
        minute: 'min',
        minutes: 'min',
        hour: 'h',
        hours: 'h',
        day: 'd',
        days: 'd',
        month: 'm',
        months: 'm',
        year: 'a',
        years: 'a',
        minuto: 'min',
        minutos: 'min',
        hora: 'h',
        horas: 'h',
        dia: 'd',
        dias: 'd',
        mes: 'm',
        meses: 'm',
        ano: 'a',
        anos: 'a',
      }
    : {
        minute: 'm',
        minutes: 'm',
        hour: 'h',
        hours: 'h',
        day: 'd',
        days: 'd',
        month: 'mo',
        months: 'mo',
        year: 'y',
        years: 'y',
      };
  const nowLabel = isPortuguese ? 'agora' : 'now';
  const timeMappings = {
    'less than a minute ago': nowLabel,
    'a minute ago': `1${unitMap.minute}${suffix}`,
    'an hour ago': `1${unitMap.hour}${suffix}`,
    'a day ago': `1${unitMap.day}${suffix}`,
    'a month ago': `1${unitMap.month}${suffix}`,
    'a year ago': `1${unitMap.year}${suffix}`,
  };
  if (normalizedTime.includes('menos de um minuto')) {
    return isPortuguese ? 'agora' : 'now';
  }
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  if (isPortuguese) {
    const sanitizedTime = normalizedTime
      .replace(/^h\u00e1\s+/i, '')
      .replace(/^em\s+/i, '')
      .replace(/cerca de |mais de |quase |aproximadamente /gi, '')
      .replace(/\bum(a)?\b/gi, '1');
    const match = sanitizedTime.match(
      /(\d+)\s+(minuto|minutos|hora|horas|dia|dias|m(?:e|\u00ea)s|meses|ano|anos|minute|minutes|hour|hours|day|days|month|months|year|years)/i
    );
    if (match) {
      const value = match[1];
      const unit = match[2].toLowerCase().replace(/\u00ea/g, 'e');
      return `${value}${unitMap[unit] || ''}${suffix}`;
    }
  }
  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `${unitMap.minute}${suffix}`)
    .replace(' minutes ago', `${unitMap.minutes}${suffix}`)
    .replace(' hour ago', `${unitMap.hour}${suffix}`)
    .replace(' hours ago', `${unitMap.hours}${suffix}`)
    .replace(' day ago', `${unitMap.day}${suffix}`)
    .replace(' days ago', `${unitMap.days}${suffix}`)
    .replace(' month ago', `${unitMap.month}${suffix}`)
    .replace(' months ago', `${unitMap.months}${suffix}`)
    .replace(' year ago', `${unitMap.year}${suffix}`)
    .replace(' years ago', `${unitMap.years}${suffix}`);
  return convertToShortTime;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
